import React, { useState, useRef } from 'react';
import { Music, Wind, RotateCcw, Sparkles, Download, Zap, ShieldCheck } from 'lucide-react';
import { useAudioEngine } from '../hooks/useAudioEngine';
import AudioPlayer from '../utils/AudioPlayer';

const NoiseRemover = ({ setIsExporting, setExportProgress }) => {
  const [audioFile, setAudioFile] = useState(null);
  const [cleanedUrl, setCleanedUrl] = useState(null);
  const previewAudioRef = useRef(null);
  
  const { removeAudioNoise, validateFileDuration } = useAudioEngine(null, null);

  const handleProcess = async (method) => {
    const url = await removeAudioNoise(audioFile, method, setIsExporting, setExportProgress);
    if (url) setCleanedUrl(url);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const check = await validateFileDuration(file, 15);
    if (!check.isValid) {
      alert(check.msg);
      return;
    }
    setAudioFile(file);
  };

  return (
    <div className="max-w-2xl mx-auto text-center space-y-10 animate-in fade-in">
      <div className="space-y-4">
        <h2 className="text-4xl font-black uppercase tracking-tighter text-black">Noise Remover</h2>
        <p className="text-slate-400">Choose your preferred cleaning method for {audioFile?.name || 'your audio'}.</p>
      </div>

      {!cleanedUrl ? (
        <div className="space-y-6">
          {!audioFile ? (
            <div className="p-20 border-2 border-dashed border-slate-800 bg-slate-900/30 rounded-[40px] hover:border-indigo-500 transition-all cursor-pointer relative group">
              <Wind className="mx-auto mb-6 text-slate-700 group-hover:text-indigo-500" size={64} />
              <input type="file" accept="audio/*" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleUpload} />
              <p className="font-bold text-slate-500 uppercase tracking-widest group-hover:text-black">Select Audio File</p>
            </div>
          ) : (
            /* --- METHOD SELECTION CARDS --- */
            <div className="grid md:grid-cols-2 gap-6 animate-in zoom-in duration-300">
              {/* STUDIO ENHANCE CARD */}
              <button 
                onClick={() => handleProcess('ai')}
                className="group relative bg-slate-900 border-2 border-indigo-500/20 hover:border-indigo-500 p-8 rounded-[40px] text-left transition-all hover:scale-[1.02]"
              >
                <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-indigo-500/20">
                  <Sparkles className="text-white" size={24} />
                </div>
                <h3 className="text-xl font-black text-white uppercase mb-2">Studio Enhance</h3>
                <p className="text-slate-400 text-xs leading-relaxed mb-6">Advanced AI isolation. Best for podcasts, interviews, and heavy street noise.</p>
                <div className="flex items-center gap-2 text-[10px] font-black text-indigo-400 uppercase tracking-widest">
                   <ShieldCheck size={14} /> AI Powered
                </div>
              </button>

              {/* STANDARD CLEAN CARD */}
              <button 
                onClick={() => handleProcess('standard')}
                className="group relative bg-slate-900 border-2 border-slate-800 hover:border-amber-500 p-8 rounded-[40px] text-left transition-all hover:scale-[1.02]"
              >
                <div className="w-12 h-12 bg-slate-800 rounded-2xl flex items-center justify-center mb-6">
                  <Zap className="text-amber-500" size={24} />
                </div>
                <h3 className="text-xl font-black text-white uppercase mb-2">Standard Clean</h3>
                <p className="text-slate-400 text-xs leading-relaxed mb-6">Fast frequency filtering. Ideal for removing light AC hum or fan hiss.</p>
                <div className="flex items-center gap-2 text-[10px] font-black text-slate-500 uppercase tracking-widest">
                   Fast Processing
                </div>
              </button>
            </div>
          )}
          
          {audioFile && (
             <button onClick={() => setAudioFile(null)} className="flex items-center gap-2 mx-auto text-slate-500 uppercase text-xs font-black hover:text-white transition-colors">
                <RotateCcw size={14} /> Change Audio File
             </button>
          )}
        </div>
      ) : (
        /* --- PREVIEW SECTION --- */
        <div className="bg-slate-900 p-10 rounded-[50px] border-2 border-indigo-500/30 space-y-8 animate-in zoom-in">
          <div className="text-center space-y-2">
            <h3 className="text-2xl font-black text-white uppercase tracking-tight">Processing Complete!</h3>
            <p className="text-slate-400 text-xs font-bold uppercase tracking-widest">Listen to the cleaned version</p>
          </div>
          
          <AudioPlayer file={cleanedUrl} audioRef={previewAudioRef} />

          <div className="grid grid-cols-2 gap-4">
            <button 
              onClick={() => { setCleanedUrl(null); setAudioFile(null); }} 
              className="p-5 bg-slate-800 text-white rounded-[2rem] font-black text-xs uppercase tracking-widest hover:bg-slate-700 transition-all"
            >
              <RotateCcw size={18} className="mx-auto mb-2" /> Reset
            </button>
            <a 
              href={cleanedUrl} 
              download={`vidabol-clean-${Date.now()}.mp3`} 
              className="p-5 bg-amber-500 text-black rounded-[2rem] font-black text-xs uppercase tracking-widest hover:scale-105 transition-all shadow-lg flex flex-col items-center justify-center"
            >
              <Download size={18} className="mb-2" /> Download MP3
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoiseRemover;